// src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";

import {
  FaUserPlus,
  FaNotesMedical,
  FaHospital,
  FaPrescriptionBottleAlt,
  FaProcedures,
  FaPills,
  FaCashRegister,
  FaBed,
  FaClipboardList,
  FaSignOutAlt,
  FaAmbulance,
  FaCut,
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaPlus,
} from "react-icons/fa";

/* ───────────────────── STYLES ───────────────────── */
const dashboardStyles = `
  .hms-dashboard {
    --hms-blue: #3b82f6;
    --hms-green: #10b981;
    --hms-orange: #f59e0b;
    --hms-pink: #ec4899;
    --hms-violet: #8b5cf6;
    --hms-teal: #14b8a6;
    --hms-red: #ef4444;
    --hms-indigo: #6366f1;
    --hms-cyan: #06b6d4;
    --hms-rose: #f43f5e;

    /* soft tints (used for the View button background) */
    --tint-blue: #eff6ff;
    --tint-green: #ecfdf5;
    --tint-orange: #fffbeb;
    --tint-pink: #fdf2f8;
    --tint-violet: #f5f3ff;
    --tint-teal: #f0fdfa;
    --tint-red: #fef2f2;
    --tint-indigo: #eef2ff;
    --tint-cyan: #ecfeff;
    --tint-rose: #fff1f2;

    --card-radius: 16px;
    --card-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 6px 16px rgba(0,0,0,0.04);
    --card-shadow-hover: 0 4px 12px rgba(0,0,0,0.08), 0 12px 28px rgba(0,0,0,0.06);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  /* ── Section Headers ── */
  .hms-section-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 12px;
    padding-left: 4px;
  }

  /* ── Metric Cards ── */
  .hms-metric-card {
    background: #fff;
    border-radius: var(--card-radius);
    box-shadow: var(--card-shadow);
    padding: 22px 24px;
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.25s ease, transform 0.25s ease;
    border: 1px solid rgba(0,0,0,0.04);
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .hms-metric-card:hover {
    box-shadow: var(--card-shadow-hover);
    transform: translateY(-2px);
  }

  /* decorative side stripe */
  .hms-metric-card::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    border-radius: 4px 0 0 4px;
  }
  .hms-metric-card.stripe-blue::before    { background: var(--hms-blue); }
  .hms-metric-card.stripe-green::before   { background: var(--hms-green); }
  .hms-metric-card.stripe-orange::before  { background: var(--hms-orange); }
  .hms-metric-card.stripe-pink::before    { background: var(--hms-pink); }
  .hms-metric-card.stripe-violet::before  { background: var(--hms-violet); }
  .hms-metric-card.stripe-teal::before    { background: var(--hms-teal); }
  .hms-metric-card.stripe-red::before     { background: var(--hms-red); }
  .hms-metric-card.stripe-indigo::before  { background: var(--hms-indigo); }
  .hms-metric-card.stripe-cyan::before    { background: var(--hms-cyan); }
  .hms-metric-card.stripe-rose::before    { background: var(--hms-rose); }

  .hms-metric-label {
    font-size: 0.78rem;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 8px;
    line-height: 1.3;
  }
  .hms-metric-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  /* icon pill */
  .hms-icon-pill {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    flex-shrink: 0;
  }
  .hms-icon-pill.bg-blue   { background: #eff6ff; color: var(--hms-blue); }
  .hms-icon-pill.bg-green  { background: #ecfdf5; color: var(--hms-green); }
  .hms-icon-pill.bg-orange { background: #fffbeb; color: var(--hms-orange); }
  .hms-icon-pill.bg-pink   { background: #fdf2f8; color: var(--hms-pink); }
  .hms-icon-pill.bg-violet { background: #f5f3ff; color: var(--hms-violet); }
  .hms-icon-pill.bg-teal   { background: #f0fdfa; color: var(--hms-teal); }
  .hms-icon-pill.bg-red    { background: #fef2f2; color: var(--hms-red); }
  .hms-icon-pill.bg-indigo { background: #eef2ff; color: var(--hms-indigo); }
  .hms-icon-pill.bg-cyan   { background: #ecfeff; color: var(--hms-cyan); }
  .hms-icon-pill.bg-rose   { background: #fff1f2; color: var(--hms-rose); }

  /* ── Card action buttons (View / Register) ── */
  .hms-card-actions {
    display: flex;
    gap: 8px;
    margin-top: auto;      /* pushes buttons to the bottom so all cards align */
    padding-top: 16px;
  }
  .hms-card-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 8px 10px;
    border-radius: 9px;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: none;
    transition: transform 0.15s ease, filter 0.15s ease;
  }
  .hms-card-btn:hover { transform: translateY(-1px); }
  .hms-card-btn.btn-view:hover { filter: brightness(0.95); }
  .hms-card-btn.btn-reg:hover  { filter: brightness(1.08); }
  .hms-card-btn:active { transform: translateY(0); }

  /* ── Bed Alert Banner ── */
  .hms-bed-alert {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border: 1px solid #fbbf24;
    border-radius: var(--card-radius);
    padding: 16px 24px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .hms-bed-alert .alert-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #f59e0b;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  .hms-bed-alert .alert-text {
    flex: 1;
    font-size: 0.88rem;
    font-weight: 600;
    color: #92400e;
  }
  .hms-bed-alert .alert-text b {
    font-size: 1.1rem;
    color: #78350f;
  }
  .hms-bed-alert .alert-action {
    font-size: 0.8rem;
    font-weight: 700;
    color: #92400e;
    text-decoration: underline;
    white-space: nowrap;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  /* ── Collection Cards (revenue) ── */
  .hms-collection-card {
    background: #fff;
    border-radius: var(--card-radius);
    box-shadow: var(--card-shadow);
    padding: 20px 24px;
    border: 1px solid rgba(0,0,0,0.04);
    transition: box-shadow 0.25s ease, transform 0.25s ease;
    height: 100%;
  }
  .hms-collection-card:hover {
    box-shadow: var(--card-shadow-hover);
    transform: translateY(-2px);
  }
  .hms-collection-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
  }
  .hms-collection-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
  }
  .hms-collection-value .currency {
    font-size: 0.9rem;
    font-weight: 600;
    color: #64748b;
    margin-right: 2px;
  }
  .hms-collection-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
  }

  /* ── Greeting Header ── */
  .hms-header {
    margin-bottom: 28px;
  }
  .hms-header h4 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px 0;
  }
  .hms-header p {
    font-size: 0.85rem;
    color: #94a3b8;
    margin: 0;
    font-weight: 400;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .hms-metric-card { padding: 16px 18px; }
    .hms-metric-value { font-size: 1.5rem; }
    .hms-icon-pill { width: 42px; height: 42px; border-radius: 12px; font-size: 1rem; }
  }
`;

/* ───────────────────── COMPONENT ───────────────────── */
export default function Home() {
  const [data, setData] = useState({});
  const [userName, setUserName] = useState("User"); 
  const navigate = useNavigate();

  useEffect(() => {
    const extractNameFromLdata = (ldataRaw) => {
      if (!ldataRaw) return null;
      try {
        const obj = typeof ldataRaw === "string" ? JSON.parse(ldataRaw) : ldataRaw;
        return obj?.fname || obj?.name || obj?.username || obj?.email || null;
      } catch (e) {
        return String(ldataRaw || "") || null;
      }
    };

    const waitForLdata = (timeout = 1000, interval = 50) =>
      new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
          const raw = sessionStorage.getItem("ldata");
          const nm = extractNameFromLdata(raw);
    if (nm) setUserName(nm);
          if (raw) return resolve(raw);
          if (Date.now() - start >= timeout) return resolve(null);
          setTimeout(tick, interval);
        };
        tick();
      });

    const showWelcomeIfNeeded = async () => {
      try {
        const raw = await waitForLdata(1000, 50);
        if (!raw) return;
        if (sessionStorage.getItem("welcomeShown")) return;
        const name = extractNameFromLdata(raw) || "User";
        toast.success(`👋 Dear ${name}, welcome to HMS System!`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        sessionStorage.setItem("welcomeShown", "1");
      } catch (e) {
        // silent
      }
    };

    const initData = async () => {
      try {
        const storedData = sessionStorage.getItem("ldata");
        if (!storedData) return;
        const response = await axios.get(AppConst.BASE_URL + AppConst.GetDashboard, {
          headers: { "Content-Type": "application/json" },
        });
        if (response?.data?.status) setData(response.data.stats || {});
        else if (response?.data?.success) toast.info(response.data.success);
      } catch (err) {
        toast.error("Failed to load dashboard data");
      }
    };

    showWelcomeIfNeeded();
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const show = (v) => (v === null || typeof v === "undefined" ? 0 : v);

  const formatCurrency = (v) => {
    const num = parseFloat(v) || 0;
    return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="page-content">
      <style>{dashboardStyles}</style>
      <div className="container-fluid hms-dashboard">

        {/* ── Page Header ── */}
        <div className="row">
          <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between bg-galaxy-transparent">
             
          <div style={{ position: "relative", width: "100%", marginBottom: "25px",marginTop: "10px" }}>
              {/* Welcome line - centered */}
              <div style={{ textAlign: "center", width: "100%" }}>
                <h4 style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: "0 0 4px 0"
                }}>
                  Welcome Dear {userName} 👋
                </h4>
              </div>

              {/* Dashboard + date - left aligned */}
              <p style={{
                fontSize: "0.85rem",
                color: "#94a3b8",
                margin: 0,
                textAlign: "left"
              }}>
                Hospital Dashboard · {today}
              </p>
              </div>
              
            
              <div style={{ position: "absolute", top: 0, right: 0 }}>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><a href="#!">Home</a></li>
                  <li className="breadcrumb-item active">Dashboard</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ TODAY'S PATIENT ACTIVITY ═══════════ */}
        <div className="hms-section-label">Today's Patient Activity</div>
        <div className="row g-3">
          <MetricCard
            title="Today LAB Patients"
            value={show(data.today_lab_patients)}
            icon={<FaNotesMedical size={20} />}
            color="green"
            viewPath="lab_patient_list"
            regPath="registrations"
          />
          <MetricCard
            title="Today OPD Patients"
            value={show(data.today_opd_patients)}
            icon={<FaPrescriptionBottleAlt size={20} />}
            color="blue"
            viewPath="opd_patient_list"
            regPath="opd_registrations"
          />
          <MetricCard
            title="Total IPD Patients"
            value={show(data.total_ipd_patients)}
            icon={<FaProcedures size={20} />}
            color="violet"
            viewPath="ipd_pataint_list"
          />
          <MetricCard
            title="Today IPD Patients"
            value={show(data.today_ipd_patients)}
            icon={<FaProcedures size={20} />}
            color="teal"
            viewPath="ipd_pataint_list"
            regPath="ipd_registrations"
          />
        </div>

        <div className="row g-3 mt-1">
          <MetricCard
            title="Today IPD Discharge"
            value={show(data.today_ipd_discharge)}
            icon={<FaSignOutAlt size={20} />}
            color="red"
            viewPath="dis_list"
          />
          <MetricCard
            title="Today Emergency"
            value={show(data.today_emergency_patients)}
            icon={<FaAmbulance size={20} />}
            color="rose"
            viewPath="emergency_list"
            regPath="emergency_reg"
          />
          <MetricCard
            title="Today Procedures"
            value={show(data.today_procedure_patients)}
            icon={<FaCut size={20} />}
            color="indigo"
            viewPath="procedure_list"
          />
          <MetricCard
            title="Today Pharmacy"
            value={show(data.today_pharmacy_patients ?? 0)}
            icon={<FaPills size={20} />}
            color="cyan"
            viewPath="sales"
              regPath="pharmacy_sale"
          />
        </div>

        {/* ═══════════ BED ALERT ═══════════ */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="hms-bed-alert">
              <div className="alert-icon">
                <FaBed size={18} />
              </div>
              <div className="alert-text">
                Only <b>{show(data.remaining_beds)}</b> of <b>{show(data.total_beds)}</b> beds available
              </div>
              <button
                type="button"
                className="alert-action"
                onClick={() => navigate("/bed_details")}
              >
                Manage Beds →
              </button>
            </div>
          </div>
        </div>

        {/* ═══════════ TODAY'S COLLECTIONS ═══════════ */}
        <div className="hms-section-label mt-4">Today's Collections</div>
        <div className="row g-3">
          <CollectionCard
            title="IPD Collections"
            value={formatCurrency(data.ipd_today_collections)}
            color="orange"
          />
          <CollectionCard
            title="LAB Collections"
            value={formatCurrency(data.lab_today_collections)}
            color="violet"
          />
          <CollectionCard
            title="OPD Collections"
            value={formatCurrency(data.opd_today_collections)}
            color="teal"
          />
          <CollectionCard
            title="Pharmacy Collections"
            value={formatCurrency(data.pharmacy_today_collections)}
            color="pink"
          />
        </div>

        {/* ═══════════ BEDS ═══════════ */}
        <div className="hms-section-label mt-4">Bed Overview</div>
        <div className="row g-3 mb-4">
          <MetricCard
            title="Total Beds"
            value={show(data.total_beds)}
            icon={<FaBed size={20} />}
            color="violet"
            viewPath="bed_details"
          />
          <MetricCard
            title="Available Beds"
            value={show(data.remaining_beds)}
            icon={<FaBed size={20} />}
            color="green"
            viewPath="bed_details"
          />
          <div className="col-md-3 d-none d-md-block" />
          <div className="col-md-3 d-none d-md-block" />
        </div>

      </div>
    </div>
  );
}

/* ── MetricCard ── */
function MetricCard({ title, value, icon, color = "blue", viewPath, regPath }) {
  const navigate = useNavigate();

  return (
    <div className="col-md-3 col-sm-6">
      <div className={`hms-metric-card stripe-${color}`}>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="hms-metric-label">{title}</div>
            <div className="hms-metric-value">{value}</div>
          </div>
          <div className={`hms-icon-pill bg-${color}`}>
            {icon}
          </div>
        </div>

        {(viewPath || regPath) && (
          <div className="hms-card-actions">
            {viewPath && (
              <button
                type="button"
                className="hms-card-btn btn-view"
                style={{ background: `var(--tint-${color})`, color: `var(--hms-${color})` }}
                onClick={() => navigate(`/${viewPath}`)}
              >
                <FaEye size={11} /> View
              </button>
            )}
            {regPath && (
              <button
                type="button"
                className="hms-card-btn btn-reg"
                style={{ background: `var(--hms-${color})`, color: "#fff" }}
                onClick={() => navigate(`/${regPath}`)}
              >
                <FaPlus size={10} /> Register
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── CollectionCard ── */
function CollectionCard({ title, value, color = "blue" }) {
  return (
    <div className="col-md-3 col-sm-6">
      <div className="hms-collection-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="hms-collection-label">{title}</div>
          <div className={`hms-collection-icon bg-${color}`} style={{ background: undefined }}>
            <FaCashRegister size={16} style={{ color: `var(--hms-${color})` }} />
          </div>
        </div>
        <div className="hms-collection-value">
          <span className="currency">₹</span>{value}
        </div>
      </div>
    </div>
  );
}